import { createAsyncThunk } from '@reduxjs/toolkit';

import { actions } from './';
import { generateAlertTrackingEvents } from '../../components/GoogleTagManager';
import { getEvrythngToken, setEvrythngKey } from '../../apis/evrythng';
import { selectAuthAlerts, selectIsQrCodeRecognized } from './selectors';
import authenticationResultsApi from '../../apis/authenticationResults';
import history from '../../history';
import identificationsApi from '../../apis/identifications';
import productApi from '../../apis/product';
import thngApi from '../../apis/thng';

export const identifyQr = createAsyncThunk(
	'scannSession/resolveQr',
	async (shortUrl, { dispatch }) => {
		const { setToastOpen, setIsLoading } = actions;
		setIsLoading(true);

		if (!window.evrythngToken) {
			// here's our one chance to get the token if it wasn't provided on load
			try {
				setEvrythngKey(await getEvrythngToken(shortUrl));
			} catch (e) {
				console.error('Could not resolve redirect');
				dispatch(setToastOpen(true));
				return;
			}
		}

		try {
			const results = await identificationsApi.identify(shortUrl);

			if (results.length > 0) {
				if (results[0].results.length === 0) {
					// irrelevant result found
					dispatch(setToastOpen(true));
				} else {
					// valid result found
					const thng = results[0].results[0].thng;
					if (thng.name === 'RESET DUPLICATE') {
					} else {
						dispatch(validateQr(thng.id));
						if (thng.product) {
							dispatch(getProduct(thng.product));
						}
					}
				}
			}
		} catch (e) {
			console.error(e);
		} finally {
			setIsLoading(false);
		}
	}
);

export const validateQr = createAsyncThunk(
	'scanSession/validateQr',
	async (thngId, { dispatch, getState }) => {
		const { setQrResults, setIsLoading } = actions;
		setIsLoading();

		let scanId;

		const socket = await authenticationResultsApi.subscribe(
			thngId,
			async (unserializableMessage) => {
				const message = JSON.parse(JSON.stringify(unserializableMessage)); // evt's package returns an instantiated class which cannot be stored in redux.

				if (message.customFields.triggerAction === scanId) {
					socket.unsubscribe();
					await dispatch(setQrResults(message));

					const isAuthenticated = selectIsQrCodeRecognized(await getState());

					const alerts = selectAuthAlerts(getState());
					if (!isAuthenticated) {
						generateAlertTrackingEvents('step1', alerts);
					}

					await history.push(
						isAuthenticated
							? '/step1/success'
							: '/verificationResults/counterfeit'
					);
				}
			}
		);

		try {
			scanId = (await thngApi.scan(thngId)).id; // trigger the scan
		} catch (e) {
			console.error(e);
		}
	}
);

export const setIsDigimarcCorrect = createAsyncThunk(
	'scanSession/setIsDigimarcCorrect',
	async (isCorrect, { dispatch, getState }) => {
		dispatch(actions.setIsDigimarcCorrect(isCorrect));

		const isAuthenticated = selectIsQrCodeRecognized(await getState());
		const alerts = selectAuthAlerts(getState());
		if (!isAuthenticated) {
			generateAlertTrackingEvents('step2', alerts);
		}
		history.push(
			`verificationResults/${isCorrect ? 'success' : 'counterfeit'}`
		);
	}
);

export const findProduct = createAsyncThunk(
	'scanSession/findProduct',
	async (thngId, { dispatch }) => {
		const { product } = await thngApi.get(thngId);
		if (product) {
			await dispatch(getProduct(product));
		}
	}
);

export const getProduct = createAsyncThunk(
	'scanSession/getProduct',
	async (productId, { dispatch }) => {
		const { setProduct } = actions;
		const product = JSON.parse(
			JSON.stringify(await productApi.get(productId).read())
		); // evt returns instantiated objects that can't be stored in the redux store, we need to clean it for storage

		await dispatch(setProduct(product));
	}
);
